import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { Checkbox } from '@atlaskit/checkbox/checkbox';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import TextArea from '@atlaskit/textarea/text-area';

import { OTHER_ID } from '../../../../../../../../common/types/form/form-fields/field-values';
import { asFormOptionField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { useFormField } from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

export const PreviewCheckboxInput = ({
	fieldId,
	fieldInputId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useFormField);
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const intl = useIntl();

	if (formOptionField === undefined) {
		return <></>;
	}

	const getCheckboxId = (optionId: string, index: number): string | undefined => {
		if (fg('jsm-reqin-fieldset-update')) {
			return `${fieldInputId}-option-${optionId}`;
		}
		return index === 0 ? fieldInputId : undefined;
	};

	return (
		<Box testId={`field-input-checkbox-preview-${fieldId}`}>
			<Stack>
				{formOptionField.options.map((option, index) => (
					<Checkbox
						key={`${fieldInputId}-${option.id}-${option.label}`}
						id={getCheckboxId(option.id, index)}
						name={
							fg('jsm-reqin-fieldset-update')
								? fieldInputId
								: `${fieldInputId}-${option.id}-${option.label}`
						}
						isChecked={formOptionField.value?.options.some((c) => c.id === option.id)}
						testId={`field-input-checkbox-preview-${fieldId}-option-${option.id}`}
						isDisabled
						label={option.label}
					/>
				))}
				{formOptionField.hasOtherOption && (
					<Checkbox
						key={`${fieldInputId}-${OTHER_ID}-${formOptionField.value?.otherOptionText ?? intl.formatMessage(messages.otherOption)}`}
						id={fg('jsm-reqin-fieldset-update') ? `${fieldInputId}-option-other` : undefined}
						name={
							fg('jsm-reqin-fieldset-update')
								? fieldInputId
								: `${fieldInputId}-${OTHER_ID}-${formOptionField.value?.otherOptionText ?? intl.formatMessage(messages.otherOption)}`
						}
						isChecked={formOptionField.value?.otherOptionText !== undefined}
						isDisabled
						label={intl.formatMessage(messages.otherOption)}
					/>
				)}
				{formOptionField.value?.otherOptionText && (
					<TextArea
						isDisabled
						value={formOptionField.value.otherOptionText}
						aria-label={intl.formatMessage(messages.otherOptionTextLabel, {
							label: formField.label,
						})}
					/>
				)}
			</Stack>
		</Box>
	);
};
